import { useTranslation } from '@unifyapps/i18n/client';
import { useSnackbar } from '@unifyapps/ui/components/Snackbar';
import { useCallback } from 'react';
import { getSelectedDataSourcePath } from '../../../../../utils/getSelectedDataSourcePath';
import type { ActionHandlerType, OnActionArgs } from '../../../../context/ActionsProvider/context';
import { useGetDataSourcesByInstanceId } from '../../../../stores/DataSourceRecordStore';
import { SHOULD_NOT_PROPAGATE_UP } from '../../const';
import type { ExportDataPayload } from './types';
import { useExportDataSource } from './useExportDataSource';
import { useExportJsonData } from './useExportJsonData';

export const useExportData = () => {
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation(['blocks']);

  const exportJsonData = useExportJsonData();
  const exportDataSource = useExportDataSource();

  const getDataSourcesByInstanceId = useGetDataSourcesByInstanceId();

  const onExportData: ActionHandlerType = useCallback(
    async (args?: OnActionArgs) => {
      const payload = args?.action.payload as ExportDataPayload | undefined;

      if (!payload?.data) {
        showSnackbar({ color: 'error', title: t('blocks:Actions.ExportData.NoData') });
        return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
      }

      const { dataSourceId } = getSelectedDataSourcePath({
        data: payload.data,
      });

      const dataSources = getDataSourcesByInstanceId(args?.instanceId);

      if (!dataSourceId || !dataSources[dataSourceId]) {
        /**
         * data is not from a datasource,
         * it can be a json array of object written inline,
         * or a variable mapping
         */
        await exportJsonData(payload, args?.actionContext ?? {});
        return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
      }

      exportDataSource(payload, args?.actionContext ?? {}, dataSources);

      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    },
    [exportDataSource, showSnackbar, t, exportJsonData, getDataSourcesByInstanceId],
  );

  return { onExportData };
};
